import React, { memo, useMemo, useRef, useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Psychology as BrainIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';

const ChatDialog = ({
  isOpen,
  onClose,
  messages,
  inputValue,
  onInputChange,
  onSendMessage,
  onKeyPress,
  isLoading,
  children,
  serviceStatus = 'online',
  loadingTime = 0
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const messagesEndRef = useRef(null);
  const dialogRef = useRef(null);
  const dragRef = useRef(null);
  
  // State for dialog position and size
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [size, setSize] = useState({ width: 900, height: 600 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [isInitialized, setIsInitialized] = useState(false);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Scroll to bottom when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [isOpen]);

  // Center dialog when it first opens
  useEffect(() => {
    if (isOpen && !isInitialized && !isMobile) {
      const centerX = (window.innerWidth - size.width) / 2;
      const centerY = (window.innerHeight - size.height) / 2;
      setPosition({ x: Math.max(0, centerX), y: Math.max(0, centerY) });
      setIsInitialized(true);
    }
  }, [isOpen, isInitialized, isMobile, size.width, size.height]);

  // Drag functionality
  const handleDragStart = useCallback((e) => {
    if (isMobile) return;
    e.preventDefault();
    e.stopPropagation();
    
    // Get the current position more accurately
    const rect = dialogRef.current?.getBoundingClientRect();
    if (rect) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  }, [isMobile]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging || isMobile) return;
    e.preventDefault();
    
    // Calculate new position based on cursor movement
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Constrain to viewport boundaries with some padding
    const padding = 20;
    const constrainedX = Math.max(padding, Math.min(newX, window.innerWidth - size.width - padding));
    const constrainedY = Math.max(padding, Math.min(newY, window.innerHeight - size.height - padding));
    
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      setPosition({
        x: constrainedX,
        y: constrainedY
      });
    });
  }, [isDragging, dragStart, isMobile, size.width, size.height]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Resize functionality
  const handleResizeStart = useCallback((e, direction = 'corner') => {
    if (isMobile) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      direction: direction
    });
  }, [size, isMobile]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || isMobile) return;
    e.preventDefault();
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    
    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    
    // Handle different resize directions
    switch (resizeStart.direction) {
      case 'corner':
        newWidth = resizeStart.width + deltaX;
        newHeight = resizeStart.height + deltaY;
        break;
      case 'right':
        newWidth = resizeStart.width + deltaX;
        break;
      case 'bottom':
        newHeight = resizeStart.height + deltaY;
        break;
      default:
        newWidth = resizeStart.width + deltaX;
        newHeight = resizeStart.height + deltaY;
    }
    
    // Constrain size to reasonable bounds
    const constrainedWidth = Math.max(400, Math.min(newWidth, window.innerWidth - position.x));
    const constrainedHeight = Math.max(300, Math.min(newHeight, window.innerHeight - position.y));
    
    setSize({
      width: constrainedWidth,
      height: constrainedHeight
    });
  }, [isResizing, resizeStart, isMobile, position.x, position.y]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add global event listeners for drag and resize
  useEffect(() => {
    if (isDragging) {
      // Add global cursor style
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        // Remove global cursor style
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  return (
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth={false}
        fullWidth={false}
        fullScreen={isMobile}
        disableScrollLock
        PaperProps={{
          ref: dialogRef,
          sx: {
            position: isMobile ? 'static' : 'fixed',
            left: isMobile ? 'auto' : `${position.x}px`,
            top: isMobile ? 'auto' : `${position.y}px`,
            width: isMobile ? '100%' : `${size.width}px`,
            height: isMobile ? '100vh' : `${size.height}px`,
            maxWidth: isMobile ? '100%' : 'none',
            maxHeight: isMobile ? '100vh' : 'none',
            minWidth: isMobile ? '100%' : '400px',
            minHeight: isMobile ? '100vh' : '300px',
            borderRadius: isMobile ? 0 : 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : (isResizing ? 'nw-resize' : 'default'),
            userSelect: 'none',
            transform: 'none !important',
            margin: 0,
            transition: (isResizing || isDragging) ? 'none' : 'all 0.2s ease',
            willChange: (isDragging || isResizing) ? 'transform' : 'auto',
            opacity: isDragging ? 0.95 : 1,
            boxShadow: isDragging ? '0 8px 32px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.1)',
            '&:hover': {
              cursor: isDragging ? 'grabbing' : (isResizing ? 'nw-resize' : 'default')
            }
          }
        }}
      >
      <DialogTitle
        ref={dragRef}
        onMouseDown={handleDragStart}
        sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          cursor: isMobile ? 'default' : (isDragging ? 'grabbing' : 'grab'),
          '&:hover': {
            cursor: isMobile ? 'default' : (isDragging ? 'grabbing' : 'grab')
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
            <BrainIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div">
              Talenty Hire - AI Assistant
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Powered by Groq ⚡
              {serviceStatus === 'busy' && ' • Service Busy'}
              {serviceStatus === 'offline' && ' • Offline Mode'}
              {!isMobile && ` • ${size.width}×${size.height}`}
            </Typography>
          </Box>
          {!isMobile && (
            <DragIcon sx={{ opacity: 0.6, cursor: 'grab' }} />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          flexDirection: 'column', 
          flex: 1,
          overflow: 'hidden'
        }}>
        {/* Quick Actions */}
        {children}
        
        {/* Messages Area */}
        <Box
          data-messages-container
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minHeight: 0,
            userSelect: 'text'
          }}
        >
          {useMemo(() => messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                mb: 1
              }}
            >
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  maxWidth: '80%',
                  bgcolor: message.type === 'user' ? '#1a237e' : 
                           message.type === 'error' ? 'error.light' : 'grey.100',
                  color: message.type === 'user' ? 'white' : 'text.primary',
                  borderRadius: 2,
                  borderTopRightRadius: message.type === 'user' ? 0 : 2,
                  borderTopLeftRadius: message.type === 'ai' ? 0 : 2,
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    userSelect: 'text',
                    cursor: 'text'
                  }}
                >
                  {message.text}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1,
                    opacity: 0.7,
                    textAlign: message.type === 'user' ? 'right' : 'left'
                  }}
                >
                  {message.timestamp.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </Typography>
              </Paper>
            </Box>
          )), [messages])}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.100' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    IA réfléchit... ({loadingTime}s/5s max) ⚡
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
          
          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={inputValue}
              onChange={onInputChange}
              onKeyPress={onKeyPress}
              placeholder="Demande-moi n'importe quoi sur le recrutement... Je suis là pour t'aider ! 😊"
              variant="outlined"
              size="small"
              disabled={isLoading}
            />
            <IconButton
              onClick={onSendMessage}
              disabled={!inputValue.trim() || isLoading}
              color="primary"
                sx={{
                  bgcolor: '#1a237e',
                  color: 'white',
                  '&:hover': { bgcolor: '#0d47a1' },
                  '&:disabled': { bgcolor: 'grey.300' }
                }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
      
      {/* Resize Handles */}
      {!isMobile && (
        <>
          {/* Bottom-right corner resize handle */}
          <Box
            onMouseDown={handleResizeStart}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 20,
              height: 20,
              cursor: 'nw-resize',
              background: 'linear-gradient(-45deg, transparent 30%, #ccc 30%, #ccc 35%, transparent 35%, transparent 65%, #ccc 65%, #ccc 70%, transparent 70%)',
              zIndex: 1000,
              '&:hover': {
                background: 'linear-gradient(-45deg, transparent 30%, #999 30%, #999 35%, transparent 35%, transparent 65%, #999 65%, #999 70%, transparent 70%)',
              }
            }}
          />
          
          {/* Right edge resize handle */}
          <Box
            onMouseDown={(e) => handleResizeStart(e, 'right')}
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 8,
              height: '100%',
              cursor: 'ew-resize',
              zIndex: 1000,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.1)',
              }
            }}
          />
          
          {/* Bottom edge resize handle */}
          <Box
            onMouseDown={(e) => handleResizeStart(e, 'bottom')}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: 8,
              cursor: 'ns-resize',
              zIndex: 1000,
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.1)',
              }
            }}
          />
        </>
      )}
    </Dialog>
  );
};

export default ChatDialog;
